import { ButtonType } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { FormikTextBox } from '~commonInfra/components/FormikField';
import { AuthenticationService } from '~src/authentication/services/AuthenticationService';
import { LoginPageStyles } from '~src/authentication/styles/LoginPageStyles';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { LoginErrorMessage } from './LoginErrorMessage';
import { UsernameAndPasswordLoginProps } from './UsernameAndPasswordLogin';


export interface UsernameAndPasswordLoginFormProps extends UsernameAndPasswordLoginProps {
  currentErrorCode?: number;
  onError?: (error: IError, username: string) => Promise<void>;
}

interface UsernameAndPasswordLoginFormFields {
  username: string;
  password: string;
}

function hasPassword(username: string): string | undefined {
  if (!username) {
    return Messages.enterYourPassword();
  }
}

function normalizeIError(error: any): IError {
  // This is really not how error should be handled. However, somehow in the development of the project,
  // some promise are rejected using ServerAPI.IError, and other simply with the error code. For example,
  // 401 errors returns only the code. To handle those scenarios, the error is coerced to either
  // error.code or simply error. In the future, it would be important to have a standard on how promises
  // can be rejected. A suggested approach is that promises should always be rejected with an instance of Error.
  const errorCode: number = error && error.code || error as number;
  const normalizedError: IError = {
    ...error,
    code: errorCode
  };
  return normalizedError;
}

export function UsernameAndPasswordLoginForm(props: UsernameAndPasswordLoginFormProps) {
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState(props.currentErrorCode ?? props.signinQueryParams.error ?? 0);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const usernameRef = React.useRef<HTMLInputElement>(null);

  function attemptLogin(values: UsernameAndPasswordLoginFormFields, formik: FormikHelpers<any>): void {
    setIsLoggingIn(true);
    setErrorCode(0);

    AuthenticationService.login(values.username ?? '', values.password, props.signinQueryParams)
      .then(() => {
        AuthenticationService.redirectAfterSuccessfulLogin(props.signinQueryParams);
      }, (error: any) => {
        const normalizedError = normalizeIError(error);
        setErrorCode(normalizedError.code);

        // Invoke the provided callback before giving back control to the user.
        const onError = props.onError ?? (() => Promise.resolve());
        onError(normalizedError, values.username).then(() => {
          setIsLoggingIn(false);
          formik.resetForm();
          formik.setValues({
            username: values.username,
            password: '' // clear out the password.
          });
          if (!values.username) {
            usernameRef.current.focus();
          } else {
            passwordRef.current.focus();
          }
        });
      });
  }

  const onUsernameChange = props.onUsernameChange || (() => { });

  // The login page really wants to load up with the username input in focus.
  /* eslint-disable jsx-a11y/no-autofocus */
  return (
    <Formik
      initialValues={{ username: props.initialUsername }}
      onSubmit={(values: UsernameAndPasswordLoginFormFields, formik: FormikHelpers<any>) => {
        attemptLogin(values, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<UsernameAndPasswordLoginFormFields>) => (
        <form onSubmit={formik.handleSubmit} data-tb-test-id='username-and-password-login-form'>
          <div>
            <div className='tb-login-text-box tb-padded'>
              <FormikTextBox
                className={LoginPageStyles.textboxStyle}
                autoFocus={true}
                name='username'
                testId='username'
                tb-test-id='textbox-username-input' // <----- Don't touch this!!! At least 6 different parts of tableau
                label={Messages.username()}         // infrastructure depend on this exact value for automation processes 
                required={null}                    // that rely on an interactive login experience.
                onClear={() => {
                  formik.setFieldValue('username', '');
                  onUsernameChange('');
                }}
                onChange={e => {
                  formik.handleChange(e);
                  onUsernameChange(e.target.value);
                }}
                ref={usernameRef}
              />
            </div>
            <div className='tb-login-text-box tb-padded'>
              <FormikTextBox
                className={LoginPageStyles.textboxStyle}
                name='password'
                testId='password'
                tb-test-id='textbox-password-input'  // <----- Don't touch this!!! At least 6 different parts of tableau
                label={Messages.password()}          // infrastructure depend on this exact value for automation processes
                required={null}                      // that rely on an interactive login experience.
                type={'password'}
                validate={hasPassword}
                onClear={() => (formik.setFieldValue('password', ''))}
                onSubmit={() => {
                  if (formik.values.password === '') {
                    setIsLoggingIn(false);
                    passwordRef.current.focus();
                }}}
                ref={passwordRef}
              />
            </div>
            <LoginErrorMessage errorCode={errorCode} />
          </div>
          <ButtonWithSpinner
            className={LoginPageStyles.submitButtonStyle}
            type='submit'
            buttonType={ButtonType.Primary}
            disabled={isLoggingIn}
            showSpinner={isLoggingIn}
            title={isLoggingIn ? Messages.signingIn() : Messages.signIn()}
            tb-test-id='button-signin'               // <----- Don't touch this either !!!
            testId='username-and-password-submit' />
        </form>
      )}
    </Formik>
  );
}
